// ProjectHeader — renders above the thread area when the user has an
// active project filter.
//
// Per designer Q12: shows project name, editable description, stats
// (session count + last active), and a "Notes from this project" section
// (deferred until memory_notes integration — placeholder for now).
//
// The description is inline-editable: click to edit, blur to save.

import { useEffect, useRef, useState } from 'react';
import type { CoachProject } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import { patchCoachProject } from '../api/endpoints.js';
import styles from './Coach.module.css';

export interface ProjectHeaderProps {
  project: CoachProject;
  onProjectUpdated: (next: CoachProject) => void;
  onClear: () => void;
}

export function ProjectHeader({ project, onProjectUpdated, onClear }: ProjectHeaderProps) {
  const { t, tt } = useI18n();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(project.description ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(project.description ?? '');
  }, [project.description]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  async function save() {
    setEditing(false);
    const next = draft.trim();
    if (next === (project.description ?? '')) return;
    const result = await patchCoachProject(project.id, { description: next });
    if (result.ok) onProjectUpdated(result.data.project);
  }

  const stats = tt('projects.stats', {
    n: String(project.sessionCount),
    date: formatLastActive(project.lastActiveAt),
  });

  return (
    <div className={styles.projectHeader}>
      {/* Back button sits ABOVE the eyebrow — easier to spot on wide
          monitors than a top-right "Clear" link, and phrased as a return
          action instead of a destructive "clear everything". */}
      <button
        type="button"
        className={styles.projectHeaderBack}
        onClick={onClear}
      >
        {t('projects.back')}
      </button>
      <div className={styles.projectHeaderEyebrow}>{t('projects.eyebrow')}</div>
      <div className={styles.projectHeaderName}>{project.name}</div>
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          className={styles.projectHeaderDescInput}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void save()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void save();
            } else if (e.key === 'Escape') {
              setDraft(project.description ?? '');
              setEditing(false);
            }
          }}
          placeholder={t('projects.desc.placeholder')}
        />
      ) : (
        <button
          type="button"
          className={styles.projectHeaderDesc}
          onClick={() => setEditing(true)}
        >
          {project.description ?? t('projects.desc.placeholder')}
        </button>
      )}
      <div className={styles.projectHeaderStats}>{stats}</div>
      {/* "Notes from this project" section is deferred — depends on
          memory_notes integration which lives in MR D (blocked on Olivier's
          confidentiality rule). Placeholder reserved for the next MR. */}
    </div>
  );
}

function formatLastActive(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
